import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Building2,
  Trash2,
  Reply,
  Archive,
  RotateCcw,
  Inbox,
  FileText,
  X,
  Plus,
  Minus,
  Search,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { formatDateTime } from '../lib/dateFormat';
import Rial from '../components/Rial';

const STATUS_META = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700' },
  responded: { label: 'Responded', cls: 'bg-emerald-100 text-emerald-700' },
  archived: { label: 'Archived', cls: 'bg-neutral-200 text-neutral-600' },
};

const AdminInquiries = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Convert-to-draft drawer state
  const [converting, setConverting] = useState(null); // inquiry or null
  const [products, setProducts] = useState([]);
  const [draftItems, setDraftItems] = useState([]); // [{id, name, size, price, qty, uom}]
  const [draftCustomer, setDraftCustomer] = useState({ name: '', company: '', email: '', phone: '', address: '' });
  const [draftNotes, setDraftNotes] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, { headers: authHeaders });
      setItems(data);
    } catch (e) {
      toast({ title: 'Failed to load inquiries', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/status`, { status }, { headers: authHeaders });
      toast({ title: `Marked as ${STATUS_META[status].label.toLowerCase()}` });
      await load();
    } catch (e) {
      toast({ title: 'Update failed', description: e.response?.data?.detail || 'Try again' });
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete inquiry from "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers: authHeaders });
      toast({ title: 'Inquiry deleted' });
      await load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again' });
    }
  };

  const openConvert = async (inq) => {
    setConverting(inq);
    setDraftCustomer({
      name: inq.name || '',
      company: inq.company || '',
      email: inq.email || '',
      phone: '',
      address: '',
    });
    setDraftNotes(inq.message || '');
    setDraftItems([]);
    setProductQuery('');
    if (products.length === 0) {
      try {
        const { data } = await axios.get(`${API}/admin/products`, { headers: authHeaders });
        setProducts(data);
      } catch {
        // ignore
      }
    }
  };

  const closeConvert = () => {
    setConverting(null);
    setDraftItems([]);
  };

  const addProductToDraft = (p) => {
    setDraftItems((prev) => {
      const ex = prev.find((x) => x.id === p.id);
      if (ex) {
        return prev.map((x) => (x.id === p.id ? { ...x, qty: x.qty + 1 } : x));
      }
      return [
        ...prev,
        {
          id: p.id,
          name: p.name,
          size: p.size || '',
          size_type: p.size_type || 'size',
          price: p.price,
          qty: p.min_qty || 1,
          uom: p.uom || 'ctn',
        },
      ];
    });
  };

  const updateDraftQty = (id, delta) => {
    setDraftItems((prev) =>
      prev.map((x) => (x.id === id ? { ...x, qty: Math.max(1, x.qty + delta) } : x)),
    );
  };

  const setDraftQty = (id, qty) => {
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n < 1) return;
    setDraftItems((prev) => prev.map((x) => (x.id === id ? { ...x, qty: n } : x)));
  };

  const removeDraftItem = (id) => {
    setDraftItems((prev) => prev.filter((x) => x.id !== id));
  };

  const draftSubtotal = draftItems.reduce((s, x) => s + x.price * x.qty, 0);

  const saveDraft = async () => {
    if (!draftCustomer.name.trim() || !draftCustomer.email.trim()) {
      toast({ title: 'Missing info', description: 'Customer name and email are required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_name: draftCustomer.name.trim(),
        customer_phone: draftCustomer.phone || '',
        customer_email: draftCustomer.email.trim(),
        customer_address: draftCustomer.address || '',
        notes: draftNotes || '',
        items: draftItems.map((i) => ({
          id: i.id,
          name: i.name,
          size: i.size,
          size_type: i.size_type || 'size',
          price: i.price,
          qty: i.qty,
          uom: i.uom,
        })),
        status: 'draft',
        inquiry_id: converting.id,
      };
      const { data } = await axios.post(`${API}/admin/orders`, payload, { headers: authHeaders });
      toast({
        title: 'Draft order created',
        description: `${data.order_number} · ${draftItems.length} item(s) · OMR ${data.total.toFixed(3)}`,
      });
      closeConvert();
      await load();
      // Take them to the orders page so they can review
      navigate('/admin/orders');
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setSaving(false);
    }
  };

  const filteredProducts = !productQuery
    ? products.slice(0, 20)
    : products.filter((p) => p.name.toLowerCase().includes(productQuery.toLowerCase())).slice(0, 20);

  const counts = {
    all: items.length,
    new: items.filter((i) => i.status === 'new').length,
    responded: items.filter((i) => i.status === 'responded').length,
    archived: items.filter((i) => i.status === 'archived').length,
  };
  const filtered = filter === 'all' ? items : items.filter((i) => i.status === filter);

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10">
        <div className="mb-6">
          <h1 className="text-3xl font-extrabold text-[#0F172A] font-display">
            Inquiries
          </h1>
          <p className="text-neutral-500 mt-1">
            Trade-account requests from the homepage form. New inquiries are also emailed to{' '}
            <span className="font-semibold text-[#0F172A]">your footer email address</span>{' '}
            (configured in Homepage settings).
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          {['all', 'new', 'responded', 'archived'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize border ${
                filter === s
                  ? 'bg-[#0F172A] text-white border-[#0F172A]'
                  : 'bg-white text-neutral-700 border-neutral-200'
              }`}
              data-testid={`filter-${s}`}
            >
              {s} ({counts[s]})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-10 text-center text-neutral-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center">
            <div className="inline-flex w-14 h-14 rounded-full bg-[#DEECF9] items-center justify-center mb-4">
              <Inbox className="w-6 h-6 text-[#0078D4]" />
            </div>
            <h3 className="text-lg font-bold text-[#0F172A]">No inquiries yet</h3>
            <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">
              When customers submit the "Request a quote" form on your homepage, their inquiries will show up here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((i) => {
              const meta = STATUS_META[i.status] || STATUS_META.new;
              return (
                <div
                  key={i.id}
                  className="bg-white rounded-2xl border border-neutral-200 p-5 lg:p-6"
                  data-testid={`inquiry-${i.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-[#0F172A]">{i.name}</h3>
                        <span className={`text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-full ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-neutral-600 flex flex-wrap gap-x-4 gap-y-1">
                        {i.company && (
                          <span className="inline-flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-neutral-400" /> {i.company}
                          </span>
                        )}
                        <a href={`mailto:${i.email}`} className="inline-flex items-center gap-1.5 hover:text-[#0078D4]">
                          <Mail className="w-3.5 h-3.5 text-neutral-400" /> {i.email}
                        </a>
                        <span className="text-neutral-400 text-xs">· {formatDateTime(i.created_at)}</span>
                      </div>
                      {i.message && (
                        <div className="mt-3 p-3 bg-neutral-50 rounded-lg text-sm text-neutral-700 whitespace-pre-wrap">
                          {i.message}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center flex-wrap gap-2 lg:flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => openConvert(i)}
                        className="h-9 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold"
                        data-testid={`convert-draft-${i.id}`}
                      >
                        <FileText className="w-3.5 h-3.5 mr-1.5" /> Convert to draft
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`mailto:${i.email}?subject=Re: Your trade inquiry`, '_blank')}
                        className="h-9 rounded-full"
                      >
                        <Reply className="w-3.5 h-3.5 mr-1.5" /> Reply
                      </Button>
                      {i.status !== 'responded' && (
                        <Button
                          size="sm"
                          onClick={() => update(i.id, 'responded')}
                          className="h-9 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
                          data-testid={`mark-responded-${i.id}`}
                        >
                          Mark responded
                        </Button>
                      )}
                      {i.status === 'archived' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => update(i.id, 'new')}
                          className="h-9 rounded-full"
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => update(i.id, 'archived')}
                          className="h-9 rounded-full"
                        >
                          <Archive className="w-3.5 h-3.5 mr-1.5" /> Archive
                        </Button>
                      )}
                      <button
                        onClick={() => remove(i.id, i.name)}
                        className="p-2 rounded-md text-red-500 hover:bg-red-50"
                        data-testid={`delete-inquiry-${i.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Convert-to-draft drawer */}
      {converting && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40" onClick={closeConvert} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col">
            <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Convert to draft order</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  From inquiry by <span className="font-semibold">{converting.name}</span>
                </p>
              </div>
              <button
                onClick={closeConvert}
                className="p-2 hover:bg-neutral-100 rounded-full"
                data-testid="close-convert"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Customer */}
              <section>
                <h4 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">
                  Customer details
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Full name *"
                    value={draftCustomer.name}
                    onChange={(e) => setDraftCustomer({ ...draftCustomer, name: e.target.value })}
                    data-testid="draft-name"
                  />
                  <Input
                    placeholder="Company"
                    value={draftCustomer.company}
                    onChange={(e) => setDraftCustomer({ ...draftCustomer, company: e.target.value })}
                  />
                  <Input
                    type="email"
                    placeholder="Email *"
                    value={draftCustomer.email}
                    onChange={(e) => setDraftCustomer({ ...draftCustomer, email: e.target.value })}
                    data-testid="draft-email"
                  />
                  <Input
                    placeholder="Phone"
                    value={draftCustomer.phone}
                    onChange={(e) => setDraftCustomer({ ...draftCustomer, phone: e.target.value })}
                  />
                  <Textarea
                    rows={2}
                    placeholder="Delivery address"
                    className="col-span-2"
                    value={draftCustomer.address}
                    onChange={(e) => setDraftCustomer({ ...draftCustomer, address: e.target.value })}
                  />
                </div>
              </section>

              {/* Product picker */}
              <section>
                <h4 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">
                  Add products
                </h4>
                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    placeholder="Search products by name…"
                    className="pl-9"
                  />
                </div>
                <div className="border border-neutral-200 rounded-lg max-h-44 overflow-y-auto bg-neutral-50">
                  {filteredProducts.length === 0 ? (
                    <div className="p-4 text-sm text-neutral-500 text-center">No products match</div>
                  ) : (
                    filteredProducts.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => addProductToDraft(p)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-white border-b border-neutral-200 last:border-0"
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-[#0F172A] truncate">{p.name}</div>
                          <div className="text-xs text-neutral-500">
                            {p.size && `${p.size} · `}<Rial /> {p.price.toFixed(3)} / {p.uom || 'ctn'}
                          </div>
                        </div>
                        <Plus className="w-4 h-4 text-[#0078D4] shrink-0" />
                      </button>
                    ))
                  )}
                </div>
              </section>

              {/* Draft items */}
              <section>
                <h4 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">
                  Draft items ({draftItems.length})
                </h4>
                {draftItems.length === 0 ? (
                  <div className="p-6 text-center text-sm text-neutral-500 bg-neutral-50 rounded-lg border border-dashed border-neutral-300">
                    No items yet. Pick from above, or save with an empty draft to add later.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {draftItems.map((it) => (
                      <div key={it.id} className="flex items-center gap-3 p-3 bg-white border border-neutral-200 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-[#0F172A] truncate">{it.name}</div>
                          <div className="text-xs text-neutral-500">
                            <Rial /> {it.price.toFixed(3)} / {it.uom}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateDraftQty(it.id, -1)}
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-neutral-200 hover:bg-neutral-50"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <Input
                            type="number"
                            min="1"
                            value={it.qty}
                            onChange={(e) => setDraftQty(it.id, e.target.value)}
                            className="h-8 w-16 text-center text-sm"
                          />
                          <button
                            onClick={() => updateDraftQty(it.id, 1)}
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-neutral-200 hover:bg-neutral-50"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-sm font-bold text-[#0F172A] w-24 text-right">
                          <Rial /> {(it.price * it.qty).toFixed(3)}
                        </div>
                        <button
                          onClick={() => removeDraftItem(it.id)}
                          className="p-1.5 rounded text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Notes (pre-filled from inquiry message) */}
              <section>
                <h4 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">
                  Internal notes
                </h4>
                <Textarea
                  rows={3}
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  placeholder="Notes for your trade desk…"
                />
              </section>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">
                  Subtotal
                </div>
                <div className="text-lg font-bold text-[#0F172A]"><Rial /> {draftSubtotal.toFixed(3)}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={closeConvert} className="h-10 rounded-full">
                  Cancel
                </Button>
                <Button
                  onClick={saveDraft}
                  disabled={saving}
                  className="h-10 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold"
                  data-testid="save-draft-order"
                >
                  {saving ? 'Saving…' : 'Create draft order'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminInquiries;
